import sortBy from 'lodash/sortBy'
import { createContext, ReactElement, ReactNode, useContext, useMemo } from 'react'

import {
  Difference,
  ReconciliationSuggestion,
  ReconciliationTransaction,
} from '@modules-deprecated/bankReconciliation/types'
import { prefixAmount } from '@modules-deprecated/bankReconciliation/utils'

import { getSubjectReconciliationDifference, getTypeFromPayableSubject } from '../utils'
import { useMultiplePayableSubjects } from './multiplePayableSubjectsContext'

interface ContextState {
  hasMatchDifference: boolean
  matchDifference: Difference
  matchSuggestion?: ReconciliationTransaction
  suggestion?: ReconciliationSuggestion
}

const MatchContext = createContext<ContextState | undefined>(undefined)

interface MatchContextContextProps {
  suggestions?: ReconciliationSuggestion[]
  children?: ReactNode
}

export const MatchContextProvider = ({ children, suggestions }: MatchContextContextProps): ReactElement => {
  const { selectedSubjectsAmountsSum, initialSubject, isCreditNoteSelected } = useMultiplePayableSubjects()

  const suggestion = useMemo(() => sortBy(suggestions, 'confidence')?.[0], [suggestions])

  const matchSuggestion = useMemo(() => suggestion?.transactions?.[0], [suggestion])

  const matchDifference = useMemo(() => {
    const reconciliationAmount = prefixAmount(matchSuggestion)

    return getSubjectReconciliationDifference({
      isCreditNote: isCreditNoteSelected,
      reconciliationAmount,
      subjectsAmount: selectedSubjectsAmountsSum,
      subjectType: getTypeFromPayableSubject(initialSubject),
    })
  }, [matchSuggestion, isCreditNoteSelected, selectedSubjectsAmountsSum, initialSubject])

  const hasMatchDifference = useMemo(() => {
    return !!matchDifference?.differenceAmount
  }, [matchDifference?.differenceAmount])

  return (
    <MatchContext.Provider
      value={{
        hasMatchDifference,
        matchDifference,
        matchSuggestion,
        suggestion,
      }}
    >
      {children}
    </MatchContext.Provider>
  )
}

export const useMatch = () => {
  const context = useContext(MatchContext)

  if (!context) {
    throw new Error('MatchContextProvider is missing in the module!')
  }

  return context
}
